import { turnItemViews, removeEmptyItemViews, fromArrayToObject } from './utils'

import { mouseDownActions } from './mouseDown'
import { mouseMoveActions } from './mouseMove'
import { mouseUpActions } from './mouseUp'

export class DrawingEngine {
  constructor() {
    this._currentInteractions = []
    this._interactions = {}
    this._status = null
    this._resizeOptions = {
      startX: 0,
      startY: 0,
      minWidth: 100,
      minHeight: 100,
      offset: 20,
      square: false,
    }
    this._rotateOptions = {
      axisX: 0,
      axisY: 0,
    }
    this._area = {
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    }
    this._itemViews = [
      {
        type: 'default',
        entity: '',
        deleted: false,
        position: 0,
        boundary: {
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          angle: 0,
        },
        params: {
          isUndeletable: true,
        },
      },
    ]
    this._render = () => {}
    this._history = []
  }

  //handler for `mouse down` event
  onMouseDown(absolutePoint) {
    let interactionDone

    //check all interactions
    this._currentInteractions.forEach(interaction => {
      if (interactionDone) return

      //search definition for current interaction
      const definition = this._interactions[interaction.name]
      if (!definition) return

      const type = definition.type
      if (!mouseDownActions[type]) return

      //launch action for current interaction
      const response = mouseDownActions[type](
        definition,
        absolutePoint,
        this._itemViews,
        interaction,
        {
          getItemViewPosition: this.getItemViewPosition.bind(this),
          getItem: this.getItem.bind(this),
          setHistory: this.setHistory.bind(this),
        },
      )

      if (response) interactionDone = true
      else return

      if (response.itemViews) this._itemViews = response.itemViews
      if (response.currentItemView) this._currentItemView = response.currentItemView
      if (response.status) this._status = response.status
      if (response.resizeOptions) this.setResizeOptions(response.resizeOptions)
    })
  }

  //handler for `mouse move` event
  onMouseMove(absolutePoint) {
    let interactionDone

    //check all interactions
    this._currentInteractions.forEach(interaction => {
      if (interactionDone) return

      //search definition for current interaction
      const definition = this._interactions[interaction.name]
      if (!definition) return

      const type = definition.type
      if (!mouseMoveActions[type]) return

      if (type !== 'focus') {
        //set resizeOptions from definition
        if (definition.resizeOptions) {
          this._resizeOptions.minWidth = definition.resizeOptions.minWidth
          this._resizeOptions.minHeight = definition.resizeOptions.minHeight
          this._resizeOptions.offset = definition.resizeOptions.offset
          this._resizeOptions.square = definition.resizeOptions.square
        }

        //launch action for current interaction
        interactionDone = mouseMoveActions[type](
          absolutePoint,
          this._status,
          this._currentItemView,
          this._resizeOptions,
          this._area,
          this._rotateOptions,
        )
      } else if (!this._status)
        //launch action for focusing
        mouseMoveActions.focus(definition, absolutePoint, this._itemViews)
    })

    //render all itemViews
    this._render(this.filterItemViews())
  }

  //handler for `mouse up` event
  onMouseUp() {
    let interactionDone

    //turn itemViews with width < 0 or height < 0
    this._itemViews = turnItemViews(this._itemViews)

    //remove itemViews wwith width == 0 or height == 0
    this._itemViews = removeEmptyItemViews(this._itemViews)

    //checking of current itemView in itemViews list
    if (this._currentItemView) {
      const currentItem = this.getItem(this._currentItemView)
      const currentItemViewPosition = this.getItemViewPosition(currentItem)
      interactionDone = currentItemViewPosition < 1
    } else interactionDone = true

    //check all interactions
    this._currentInteractions.forEach(interaction => {
      if (interactionDone) return

      //search definition for current interaction
      const definition = this._interactions[interaction.name]
      if (!definition) return

      const type = definition.type
      if (!mouseUpActions[type]) return

      //launch action for current interaction
      interactionDone = mouseUpActions[type](definition, this._status, this._currentItemView, {
        getItem: this.getItem.bind(this),
      })
    })

    this._status = null

    //render all itemViews
    this._render(this.filterItemViews())
  }

  //set interaction definitions
  registerInteractions(interactionDefinitions) {
    this._interactions = fromArrayToObject(interactionDefinitions, 'name')
  }

  setMouseListeners(mouseListeners) {
    mouseListeners({
      onMouseDown: this.onMouseDown.bind(this),
      onMouseMove: this.onMouseMove.bind(this),
      onMouseUp: this.onMouseUp.bind(this),
    })
  }

  setRenderListener(renderListener) {
    this._render = renderListener
  }

  setInteractions(interactions) {
    this._currentInteractions = interactions.map(interaction => {
      return { ...interaction }
    })
  }

  //get top item from ItemViews
  getTopItem(): Item {
    let maxPosition = 0
    let topItemView = this._itemViews[0]
    this._itemViews
      .filter(item => !item.deleted)
      .forEach(item => {
        if (item.position >= maxPosition) maxPosition = item.position
        topItemView = item
      })

    return this.getItem(topItemView)
  }

  //get rooot Item from itemViews
  getRootItem() {
    return this.getItem(this._itemViews[0])
  }

  //create new itemView
  drawItem(options: DrawItemOptions) {
    //get index of mount item
    let position = this.getItemViewPosition(options.mountItem) + 1
    if (position < 1) position = 0

    const newItemView = {
      type: 'default',
      entity: options.entity,
      deleted: false,
      position: position,
      boundary: options.boundary,
      params: options.params || {},
    }

    this._itemViews.forEach(item => {
      if (!item.deleted && item.position >= position) item.position += 1
    })

    this._itemViews.push(newItemView)

    this.setHistory({
      type: 'draw',
      itemView: newItemView,
      prevValue: false,
    })

    //render all itemViews
    this._render(this.filterItemViews())
    return this.getItem(newItemView)
  }

  removeItem(item) {
    const position = this.getItemViewPosition(item)
    const deletedItem = this._itemViews.find(itemView => itemView.position === position)
    if (deletedItem) {
      deletedItem.type = 'default'
      deletedItem.deleted = true

      this.setHistory({
        type: 'delete',
        itemView: deletedItem,
        prevValue: false,
      })
    }
  }

  //get item for current itemView
  getItem(itemView) {
    //get boundary of current itemView
    const getBoundary = () => {
      return { ...itemView.boundary }
    }

    const setBoundary = boundary => {
      this.setHistory({
        type: 'resize',
        itemView,
        prevValue: { ...itemView.boundary },
        prevStep: true,
      })
      itemView.boundary = boundary
    }

    //change type of current itemView
    const toggleSelection = toggle => {
      itemView.type = toggle ? 'selected' : 'default'
    }

    //remove current itemView
    const remove = () => {
      if (itemView.params.isUndeletable) return

      itemView.type = 'default'
      itemView.deleted = true

      this.setHistory({
        type: 'delete',
        itemView,
        prevValue: false,
      })
    }

    //get deleted status of itemView
    const getStatus = () => (itemView.deleted ? 'deleted' : itemView.type)

    const getParams = () => {
      return { ...itemView.params }
    }

    const setParams = params => {
      this.setHistory({
        type: 'params',
        itemView,
        prevValue: { ...itemView.params },
      })
      itemView.params = params
    }

    const item = {
      getBoundary,
      setBoundary,
      remove,
      toggleSelection,
      getStatus,
      getParams,
      setParams,
    }
    return item
  }

  //get position of itemView for current item
  getItemViewPosition(item) {
    let position = -1

    if (!item.getBoundary) return position

    const boundary = item.getBoundary()

    this._itemViews.forEach(e => {
      if (
        !e.deleted &&
        e.boundary.x === boundary.x &&
        e.boundary.y === boundary.y &&
        e.boundary.width === boundary.width &&
        e.boundary.height === boundary.height
      )
        position = e.position
    })

    return position
  }

  //get itemView for current item
  getItemView(item) {
    if (!item.getBoundary) return undefined

    const boundary = item.getBoundary()

    return this._itemViews.find(
      e =>
        e.boundary.x === boundary.x &&
        e.boundary.y === boundary.y &&
        e.boundary.width === boundary.width &&
        e.boundary.height === boundary.height,
    )
  }

  setStatus(value) {
    this._status = value
  }

  getStatus() {
    return this._status
  }

  //set values of area properties
  setArea(value) {
    Object.keys(this._area).forEach(key => {
      if (value.hasOwnProperty(key)) this._area[key] = value[key]
    })
  }

  getArea() {
    return this._area
  }

  //set values of resizeOptions properties
  setResizeOptions(value) {
    Object.keys(this._resizeOptions).forEach(key => {
      if (value.hasOwnProperty(key)) this._resizeOptions[key] = value[key]
    })
  }

  getResizeOptions() {
    return this._resizeOptions
  }

  //set values of rotateOptions properties
  setRotateOptions(value) {
    Object.keys(this._rotateOptions).forEach(key => {
      if (value.hasOwnProperty(key)) this._rotateOptions[key] = value[key]
    })
  }

  //get rotateOptions
  getRotateOptions() {
    return this._rotateOptions
  }

  //rerender all itemViews
  reRender() {
    this._render(this.filterItemViews())
  }

  //filter itemView by deleted, and sorting itemViews by position
  filterItemViews() {
    return this._itemViews
      .filter(itemView => !itemView.deleted)
      .sort((a, b) => a.position - b.position)
  }

  //add new point to history
  setHistory(value: History) {
    this._history.push(value)
  }

  //get history
  getHistory() {
    return this._history
  }

  //cancel last change
  undo() {
    if (!this._history.length) return

    const { type, itemView, prevValue, prevStep = false } = this._history.pop()

    //check for deleted empty itemView
    if (!this._itemViews.find(item => item === itemView)) return this.undo()

    switch (type) {
      case 'drag':
      case 'resize':
        itemView.boundary = prevValue
        break
      case 'draw':
        itemView.deleted = true
        break
      case 'delete':
        itemView.deleted = prevValue
        break
      case 'params':
        itemView.params = prevValue
        break
    }

    this._itemViews.forEach(item => {
      if (item.type === 'focused') item.type = 'default'
    })

    //yet one step to back
    if (prevStep) return this.undo()

    this._render(this.filterItemViews())
  }

  //deleted selected entity
  clear(entity) {
    this._itemViews.forEach(itemView => {
      if (!itemView.deleted && itemView.entity === entity) {
        itemView.deleted = true
        this.setHistory({
          type: 'delete',
          itemView,
          prevValue: false,
        })
      }
    })
  }
}



// WEBPACK FOOTER //
// ./src/drawing/core/DrawingEngine.js